import { FunctionComponent, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { fetchDocsPage } from '../../../Api/Docs';
import { DocsPage } from '../../../models/docs';
import { DocsMarkdown } from './DocsMarkdown';
import { DocsTocEntry, extractToc } from './docsHeadings';

interface DocsContentProps {
  slug: string;
  onTocChange: (entries: DocsTocEntry[]) => void;
  // Non-production docs role simulation; forwarded to the page and image fetches so
  // access, link redaction and images resolve as the simulated role. Empty means none.
  simulateRole?: string;
}

// Fetches and renders a single docs page for the given slug. Reports the page's
// heading structure up to the shell so the right-hand TOC can render.
export const DocsContent: FunctionComponent<DocsContentProps> = ({ slug, onTocChange, simulateRole }) => {
  const [page, setPage] = useState<DocsPage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setPage(null);
    onTocChange([]);

    if (!slug) {
      setLoading(false);
      return;
    }

    fetchDocsPage(slug, simulateRole, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.error || !result.data) {
        setError(result.error?.message || 'This documentation page could not be loaded.');
      } else {
        setPage(result.data);
        onTocChange(extractToc(result.data.markdown));
      }
      setLoading(false);
    });

    return () => controller.abort();
  }, [slug, simulateRole]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!page) {
    return (
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary' }}>
        Select a page from the navigation to get started.
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: 820 }}>
      <DocsMarkdown
        markdown={page.markdown}
        simulateRole={simulateRole}
      />
    </Box>
  );
};
